import React, { useState, useContext, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { loginSeller } from '../../services/sellerService';
import { AuthContext } from '../../contexts/AuthContext';

const LoginSchema = Yup.object().shape({
  email: Yup.string().email('Invalid email address').required('Email is required'),
  password: Yup.string().min(6, 'Password must be at least 6 characters').required('Password is required'),
});

const SellerLogin = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { loginSeller: authLoginSeller, sellerAuth } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  // 🎯 CRITICAL FIX: Check if already authenticated and redirect
  useEffect(() => {
    console.log('🔍 [SellerLogin] Checking authentication state:', {
      isAuthenticated: sellerAuth?.isAuthenticated,
      hasSeller: !!sellerAuth?.seller,
      sellerName: sellerAuth?.seller?.firstName,
      currentPath: location.pathname
    });

    if (sellerAuth?.isAuthenticated && sellerAuth?.seller) {
      console.log('✅ [SellerLogin] Seller already authenticated, redirecting to dashboard');
      const redirectTo = location.state?.from?.pathname || '/seller/dashboard';
      navigate(redirectTo, { replace: true });
    }
  }, [sellerAuth?.isAuthenticated, sellerAuth?.seller, navigate, location.state?.from]);

  const handleSubmit = async (values, { setSubmitting }) => {
    setIsLoading(true);
    try {
      console.log('🔄 [SellerLogin] Attempting seller login...');
      const response = await loginSeller(values);

      if (response.success) {
        console.log('✅ [SellerLogin] Server login successful, updating context...');
        await authLoginSeller(response.data);
        toast.success('Login successful!');

        // 🎯 FIXED: Wait for state update before navigation
        setTimeout(() => {
          const redirectTo = location.state?.from?.pathname || '/seller/dashboard';
          console.log('🎯 [SellerLogin] Redirecting to:', redirectTo);
          navigate(redirectTo, { replace: true });
        }, 100);
      } else {
        toast.error(response.message || 'Login failed');
      }
    } catch (error) {
      console.error('❌ [SellerLogin] Login error:', error);
      toast.error(error.message || 'Something went wrong');
    } finally {
      setIsLoading(false);
      setSubmitting(false);
    }
  };

  const inputClasses =
    'block w-full px-4 py-3.5 bg-white/70 border-2 border-gray-200 rounded-xl shadow-sm ' +
    'focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent ' +
    'transition-all duration-200 backdrop-blur-sm text-gray-800 placeholder-gray-400';

  return (
    <div className="relative overflow-hidden min-h-screen bg-gradient-to-br from-gray-50 via-white to-orange-50/30 flex items-center justify-center p-4">
      {/* Decorative background blobs */}
      <div className="pointer-events-none absolute top-[-6rem] right-[-6rem] w-72 h-72 bg-orange-300/30 rounded-full blur-3xl"></div>
      <div className="pointer-events-none absolute bottom-[-6rem] left-[-6rem] w-72 h-72 bg-amber-300/20 rounded-full blur-3xl"></div>

      <div className="relative w-full max-w-md animate-fade-in-up">
        {/* Gradient glow behind the glass card */}
        <div className="absolute -inset-1 bg-gradient-to-r from-orange-500 via-orange-600 to-amber-500 rounded-3xl blur opacity-20"></div>

        <div className="relative bg-white/80 backdrop-blur-sm rounded-3xl shadow-2xl border border-white/50 p-8 sm:p-10">
          {/* Logo */}
          <div className="flex flex-col items-center mb-6">
            <img
              src="/images/zammer-official-logo.png"
              alt="ZAMMER"
              className="h-16 w-auto object-contain mb-4"
            />
            <h1 className="text-3xl font-bold bg-gradient-to-r from-orange-500 via-orange-600 to-orange-700 bg-clip-text text-transparent text-center">
              Welcome Back Seller!
            </h1>
            <p className="text-gray-500 text-center mt-2">Login to manage your shop</p>
          </div>

          <Formik
            initialValues={{ email: '', password: '' }}
            validationSchema={LoginSchema}
            onSubmit={handleSubmit}
          >
            {({ isSubmitting }) => (
              <Form className="space-y-5">
                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                    Email Address
                  </label>
                  <Field
                    id="email"
                    name="email"
                    type="email"
                    placeholder="Enter your email"
                    className={inputClasses}
                  />
                  <ErrorMessage name="email" component="div" className="text-red-500 text-sm mt-1" />
                </div>

                {/* Password with show/hide */}
                <div>
                  <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <Field
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter your password"
                      className={inputClasses + ' pr-12'}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((s) => !s)}
                      className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-orange-500 transition-colors"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                  </div>
                  <ErrorMessage name="password" component="div" className="text-red-500 text-sm mt-1" />
                </div>

                {/* Forgot password */}
                <div className="text-right">
                  <Link
                    to="/seller/forgot-password"
                    className="text-sm text-orange-600 hover:text-orange-700 font-semibold transition-colors"
                  >
                    Forgot Password?
                  </Link>
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={isSubmitting || isLoading}
                  className="w-full flex items-center justify-center bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white py-3.5 px-6 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isLoading && (
                    <span className="w-5 h-5 mr-2 border-2 border-white/40 border-t-white rounded-full animate-spin inline-block"></span>
                  )}
                  {isLoading ? 'Signing in...' : 'Sign In'}
                </button>

                {/* Register link */}
                <p className="text-center text-sm text-gray-500 pt-2">
                  Don't have an account?{' '}
                  <Link to="/seller/register" className="text-orange-600 hover:text-orange-700 font-semibold transition-colors">
                    Register
                  </Link>
                </p>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
};

export default SellerLogin;
